import axios from 'axios';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:3402';

export interface PostTemplate {
  id: string;
  creatorId: string;
  name: string;
  description?: string;
  category?: string;
  title: string;
  content: string;
  variables?: string[];
  tierAccess?: string[];
  tags?: string[];
  useCount: number;
  isPublic: boolean;
  createdAt: Date;
  updatedAt: Date;
}

export interface CreateTemplateData {
  name: string;
  description?: string;
  category?: string;
  title: string;
  content: string;
  variables?: string[];
  tierAccess?: string[];
  tags?: string[];
  isPublic?: boolean;
}

const postTemplateService = {
  // Create template
  create: async (data: CreateTemplateData): Promise<PostTemplate> => {
    const response = await axios.post(`${API_URL}/api/post-templates`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get all templates
  getAll: async (params?: {
    category?: string;
    isPublic?: boolean;
  }): Promise<PostTemplate[]> => {
    const response = await axios.get(`${API_URL}/api/post-templates`, {
      params,
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Get single template
  getById: async (id: string): Promise<PostTemplate> => {
    const response = await axios.get(`${API_URL}/api/post-templates/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Update template
  update: async (id: string, data: Partial<CreateTemplateData>): Promise<PostTemplate> => {
    const response = await axios.patch(`${API_URL}/api/post-templates/${id}`, data, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data;
  },

  // Delete template
  delete: async (id: string): Promise<void> => {
    await axios.delete(`${API_URL}/api/post-templates/${id}`, {
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
  },

  // Use template (increment use count)
  use: async (id: string, variables?: Record<string, string>): Promise<{ title: string; content: string }> => {
    const response = await axios.post(
      `${API_URL}/api/post-templates/${id}/use`,
      { variables },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data;
  },

  // Get popular public templates
  getPopular: async (limit?: number): Promise<PostTemplate[]> => {
    const response = await axios.get(`${API_URL}/api/post-templates/popular`, {
      params: { limit },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.templates;
  },

  // Search templates
  search: async (keyword: string): Promise<PostTemplate[]> => {
    const response = await axios.get(`${API_URL}/api/post-templates/search`, {
      params: { q: keyword },
      headers: {
        Authorization: `Bearer ${localStorage.getItem('token')}`,
      },
    });
    return response.data.templates;
  },

  // Duplicate template
  duplicate: async (id: string, name?: string): Promise<PostTemplate> => {
    const response = await axios.post(
      `${API_URL}/api/post-templates/${id}/duplicate`,
      { name },
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.template;
  },

  // Create post from template
  createPost: async (params: {
    templateId: string;
    variables?: Record<string, string>;
    title?: string;
    tierIds?: string[];
    tags?: string[];
    isPublic?: boolean;
    isScheduled?: boolean;
    publishedAt?: string;
  }): Promise<any> => {
    const response = await axios.post(
      `${API_URL}/api/post-templates/create-post`,
      params,
      {
        headers: {
          Authorization: `Bearer ${localStorage.getItem('token')}`,
        },
      }
    );
    return response.data.post;
  },
};

export default postTemplateService;
